import {
  ACESFilmicToneMapping,
  Color,
  FogExp2,
  PerspectiveCamera,
  Scene,
  Vector2,
  WebGLRenderer,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { CompositeShader } from '../shaders/composite.js';

/**
 * Renderer, scene, camera and the post chain. Nothing about Isidora lives
 * here — only the apparatus that makes it visible.
 */
export class Stage {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {ReturnType<import('../config/settings.js').resolveSettings>} settings
   */
  constructor(canvas, settings) {
    this.canvas = canvas;
    this.settings = settings;

    // `?capture` keeps the drawing buffer around after the swap. Without it a
    // headless screenshot of a WebGL canvas comes back black at random, which
    // looks exactly like a scene bug and is not one. It costs a copy per
    // frame, so it is never on for a real visitor.
    const capture = new URLSearchParams(globalThis.location?.search ?? '').has('capture');

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: settings.tier === 'high',
      powerPreference: 'high-performance',
      stencil: false,
      preserveDrawingBuffer: capture,
    });
    this.renderer.setClearColor(0x05070f, 1);
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = settings.post.exposure;
    // The composer runs several passes per frame; let the counters accumulate
    // across all of them so the inspector reports the real frame cost.
    this.renderer.info.autoReset = false;

    this.maxPixelRatio = settings.tier === 'low' ? 1.25 : settings.tier === 'medium' ? 1.6 : 2;

    this.scene = new Scene();
    this.scene.fog = new FogExp2(new Color(0x1c2a44), settings.atmosphere.fogDensity);

    this.camera = new PerspectiveCamera(
      settings.camera.fov,
      1,
      settings.camera.near,
      settings.camera.far,
    );
    this.camera.position.set(0, 0, 0);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.bloom = new UnrealBloomPass(
      new Vector2(1, 1),
      settings.post.bloomStrength,
      settings.post.bloomRadius,
      settings.post.bloomThreshold,
    );
    this.composer.addPass(this.bloom);

    this.composite = new ShaderPass(CompositeShader);
    this.composite.uniforms.uVignette.value = settings.post.vignette;
    this.composite.uniforms.uGrain.value = settings.post.grain;
    this.composite.uniforms.uAberration.value = settings.post.aberration;
    this.composer.addPass(this.composite);

    this.composer.addPass(new OutputPass());

    this.size = new Vector2(1, 1);
    this.resize();
  }

  /** @returns {{width:number, height:number, dpr:number}} */
  resize() {
    const width = Math.max(1, this.canvas.clientWidth || globalThis.innerWidth || 1);
    const height = Math.max(1, this.canvas.clientHeight || globalThis.innerHeight || 1);
    const dpr = Math.min(globalThis.devicePixelRatio || 1, this.maxPixelRatio);

    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(width, height);
    this.bloom.setSize(width * dpr, height * dpr);

    this.camera.aspect = width / height;
    // Keep a tall phone from cropping the stairwell out of frame. The rig
    // reads `baseFov` so a telescope still narrows from the right starting
    // angle on a portrait screen.
    this.baseFov = this.settings.camera.fov * (this.camera.aspect < 0.85 ? 1.22 : 1);
    this.camera.fov = this.baseFov;
    this.camera.updateProjectionMatrix();

    this.composite.uniforms.uResolution.value.set(width * dpr, height * dpr);
    this.size.set(width, height);
    return { width, height, dpr };
  }

  render(elapsed) {
    this.composite.uniforms.uTime.value = elapsed;
    this.lastInfo = {
      calls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      geometries: this.renderer.info.memory.geometries,
      textures: this.renderer.info.memory.textures,
      programs: this.renderer.info.programs?.length ?? 0,
    };
    this.renderer.info.reset();
    this.composer.render();
  }

  /** The previous frame's totals, across every pass. */
  get info() {
    return this.lastInfo ?? { calls: 0, triangles: 0, geometries: 0, textures: 0, programs: 0 };
  }

  dispose() {
    this.composer.dispose();
    this.renderer.dispose();
  }
}
